import { useState, useEffect } from 'react';
import {
  Box, Button, Stack, TextField, Typography, CircularProgress,
  Paper, Divider, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Calculate as CalcIcon, Save as SaveIcon,
  Block as AnularIcon, Visibility as VerIcon,
} from '@mui/icons-material';
import { LiquidacionService } from '../services/liquidacionService';
import type { LiquidacionCalculo, LiquidacionBeneficios } from '../types/liquidacion';

export default function LiquidacionPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idTrabajador, setIdTrabajador] = useState(0);
  const [fechaCese, setFechaCese] = useState(new Date().toISOString().split('T')[0]);
  const [calculo, setCalculo] = useState<LiquidacionCalculo | null>(null);
  const [liquidaciones, setLiquidaciones] = useState<LiquidacionBeneficios[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLiq, setSelectedLiq] = useState<LiquidacionBeneficios | null>(null);
  const [observaciones, setObservaciones] = useState('');

  useEffect(() => {
    loadLiquidaciones();
  }, []);

  const loadLiquidaciones = async () => {
    try {
      const data = await LiquidacionService.getAll();
      setLiquidaciones(data);
    } catch { /* handled by interceptor */ }
  };

  const handleCalcular = async () => {
    if (!idTrabajador) return;
    setLoading(true);
    setError(null);
    try {
      const result = await LiquidacionService.calcular(idTrabajador, fechaCese);
      setCalculo(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error calculando liquidación');
    } finally {
      setLoading(false);
    }
  };

  const handleGrabar = async () => {
    if (!calculo) return;
    setLoading(true);
    try {
      await LiquidacionService.grabar({
        idTrabajador: calculo.idTrabajador,
        fechaCese,
        idMotivoCese: null,
        observaciones: observaciones || null,
      });
      setCalculo(null);
      setObservaciones('');
      await loadLiquidaciones();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error grabando liquidación');
    } finally {
      setLoading(false);
    }
  };

  const handleAnular = async (id: number) => {
    if (!confirm('Anular esta liquidación reactivará al trabajador.')) return;
    try {
      await LiquidacionService.anular(id);
      await loadLiquidaciones();
    } catch { /* handled */ }
  };

  const handleVerDetalle = async (id: number) => {
    const liq = await LiquidacionService.getById(id);
    setSelectedLiq(liq);
    setDetailOpen(true);
  };

  const fmt = (n: number) => `S/ ${n.toFixed(2)}`;

  const columns: GridColDef[] = [
    { field: 'idLiquidacion', headerName: 'ID', width: 60 },
    { field: 'idTrabajador', headerName: 'Trab.', width: 70 },
    { field: 'fechaCese', headerName: 'Fecha Cese', width: 110,
      valueFormatter: (v: string) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'remuneracionComputable', headerName: 'Rem. Comp.', width: 120, type: 'number',
      valueFormatter: (v: number) => v?.toFixed(2) },
    { field: 'ctsTrunca', headerName: 'CTS', width: 100, type: 'number',
      valueFormatter: (v: number) => v?.toFixed(2) },
    { field: 'gratificacionTrunca', headerName: 'Grat.', width: 100, type: 'number',
      valueFormatter: (v: number) => v?.toFixed(2) },
    { field: 'vacacionesTruncas', headerName: 'Vac.', width: 100, type: 'number',
      valueFormatter: (v: number) => v?.toFixed(2) },
    { field: 'totalBruto', headerName: 'Bruto', width: 110, type: 'number',
      valueFormatter: (v: number) => v?.toFixed(2) },
    { field: 'descuentoPrestamos', headerName: 'Desc. Prést.', width: 110, type: 'number',
      valueFormatter: (v: number) => v?.toFixed(2) },
    { field: 'totalNeto', headerName: 'Neto', width: 110, type: 'number',
      valueFormatter: (v: number) => v?.toFixed(2), cellClassName: 'font-bold' },
    { field: 'estado', headerName: 'Estado', width: 100,
      renderCell: (params) => {
        const color = params.value === 'CALCULADA' ? 'primary' : params.value === 'PAGADA' ? 'success' : 'error';
        return <Chip label={params.value} size="small" color={color} />;
      }},
    { field: 'acciones', headerName: '', width: 90, sortable: false,
      renderCell: (params) => (
        <Stack direction="row">
          <IconButton size="small" onClick={() => handleVerDetalle(params.row.idLiquidacion)}><VerIcon fontSize="small" /></IconButton>
          {params.row.estado === 'CALCULADA' && (
            <IconButton size="small" color="error" onClick={() => handleAnular(params.row.idLiquidacion)}><AnularIcon fontSize="small" /></IconButton>
          )}
        </Stack>
      )},
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Liquidación de Beneficios Sociales
      </Typography>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {/* Calculadora */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField size="small" label="ID Trabajador" type="number" value={idTrabajador || ''}
            onChange={(e) => setIdTrabajador(Number(e.target.value))} sx={{ width: 140 }} />
          <TextField size="small" label="Fecha de Cese" type="date" value={fechaCese}
            onChange={(e) => setFechaCese(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 170 }} />
          <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <CalcIcon />}
            onClick={handleCalcular} disabled={loading || !idTrabajador}>
            Calcular Liquidación
          </Button>
        </Stack>

        {calculo && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {calculo.nombreTrabajador}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ingreso: {new Date(calculo.fechaIngreso).toLocaleDateString('es-PE')} |
              Cese: {new Date(calculo.fechaCese).toLocaleDateString('es-PE')} |
              Rem. Computable: {fmt(calculo.remuneracionComputable)}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {/* CTS */}
              <Box>
                <Typography variant="subtitle2" color="primary">CTS Trunca</Typography>
                <Typography variant="body2">{calculo.ctsDetalle}</Typography>
                <Typography variant="h6">{fmt(calculo.ctsTrunca)}</Typography>
              </Box>

              {/* Gratificación */}
              <Box>
                <Typography variant="subtitle2" color="primary">Gratificación Trunca</Typography>
                <Typography variant="body2">{calculo.gratDetalle}</Typography>
                <Typography variant="body2">Bonif. Extra (9%): {fmt(calculo.bonificacionExtraordinaria)}</Typography>
                <Typography variant="h6">{fmt(calculo.gratificacionTrunca + calculo.bonificacionExtraordinaria)}</Typography>
              </Box>

              {/* Vacaciones */}
              <Box>
                <Typography variant="subtitle2" color="primary">Vacaciones Truncas</Typography>
                <Typography variant="body2">{calculo.vacDetalle}</Typography>
                <Typography variant="h6">{fmt(calculo.vacacionesTruncas)}</Typography>
              </Box>

              <Divider />

              {/* Totales */}
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Bruto</Typography>
                  <Typography variant="h6">{fmt(calculo.totalBruto)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Desc. Préstamos</Typography>
                  <Typography variant="h6" color="error">{fmt(calculo.descuentoPrestamos)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Neto</Typography>
                  <Typography variant="h5" fontWeight={700} color="success.main">{fmt(calculo.totalNeto)}</Typography>
                </Box>
              </Stack>

              <TextField size="small" label="Observaciones" multiline rows={2} value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)} fullWidth />

              <Button variant="contained" color="success" startIcon={<SaveIcon />}
                onClick={handleGrabar} disabled={loading} sx={{ alignSelf: 'flex-start' }}>
                Grabar Liquidación y Cesar Trabajador
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Historial */}
      <Typography variant="h6" sx={{ mb: 1 }}>Liquidaciones Registradas</Typography>
      <DataGrid
        rows={liquidaciones}
        columns={columns}
        getRowId={(row) => row.idLiquidacion}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        pageSizeOptions={[25]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />

      {/* Detalle Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle Liquidación #{selectedLiq?.idLiquidacion}</DialogTitle>
        <DialogContent>
          {selectedLiq && (
            <Table size="small">
              <TableBody>
                <TableRow><TableCell>Trabajador</TableCell><TableCell>{selectedLiq.idTrabajador}</TableCell></TableRow>
                <TableRow><TableCell>Fecha Cese</TableCell><TableCell>{new Date(selectedLiq.fechaCese).toLocaleDateString('es-PE')}</TableCell></TableRow>
                <TableRow><TableCell>Rem. Computable</TableCell><TableCell>{fmt(selectedLiq.remuneracionComputable)}</TableCell></TableRow>
                <TableRow><TableCell>CTS Trunca</TableCell><TableCell>{fmt(selectedLiq.ctsTrunca)} ({selectedLiq.ctsMesesComputables}m {selectedLiq.ctsDiasComputables}d)</TableCell></TableRow>
                <TableRow><TableCell>Gratificación Trunca</TableCell><TableCell>{fmt(selectedLiq.gratificacionTrunca)} ({selectedLiq.gratMesesComputables}m)</TableCell></TableRow>
                <TableRow><TableCell>Bonif. Extraordinaria</TableCell><TableCell>{fmt(selectedLiq.bonificacionExtraordinaria)}</TableCell></TableRow>
                <TableRow><TableCell>Vacaciones Truncas</TableCell><TableCell>{fmt(selectedLiq.vacacionesTruncas)} ({selectedLiq.vacDiasTruncos}d)</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Total Bruto</TableCell><TableCell sx={{ fontWeight: 600 }}>{fmt(selectedLiq.totalBruto)}</TableCell></TableRow>
                <TableRow><TableCell>Desc. Préstamos</TableCell><TableCell color="error">{fmt(selectedLiq.descuentoPrestamos)}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Neto</TableCell><TableCell sx={{ fontWeight: 700, color: 'green' }}>{fmt(selectedLiq.totalNeto)}</TableCell></TableRow>
                <TableRow><TableCell>Estado</TableCell><TableCell>{selectedLiq.estado}</TableCell></TableRow>
                {selectedLiq.observaciones && <TableRow><TableCell>Observaciones</TableCell><TableCell>{selectedLiq.observaciones}</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          {selectedLiq?.detalleCalculo && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Detalle de Cálculo</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                {selectedLiq.detalleCalculo}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
